<template>
    <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between">
            <div class="text-2xl font-medium text-gray-900">
                Recurring Billing Details
            </div>

            <prime-button
                text
                icon="pi pi-pencil"
                label="Edit"
                :class="showEditButton ? '' : 'invisible'"
                @click="handleEditButton"
            />
        </div>

        <div class="space-y-4">
            <div v-if="recurringTotal.isPositiveAndNotZero()">
                <span class="font-semibold">Recurring Dues:</span
                >&nbsp;&nbsp;<span class="text-lg">{{
                    recurringTotal.formatCurrency()
                }}</span>
                <br />
                <div class="text-xs italic">
                    *Sales tax will be calculated at the time of billing
                </div>
            </div>

            <div
                v-html="storedPaymentInformation.recurringAuthorizationMarkup"
            />

            <div
                v-if="recurringInformationOptions.length > 1"
                class="flex flex-col flex-wrap gap-1"
            >
                <div
                    v-for="option in recurringInformationOptions"
                    :key="option.key"
                    class="flex items-center"
                >
                    <radio-button
                        v-model="recurringInformationChoice"
                        :value="option.key"
                        :input-id="option.key"
                        name="recurring-information"
                    />
                    <label
                        :for="option.key"
                        class="ml-2"
                    >
                        {{ option.text }}
                    </label>
                </div>
            </div>

            <hosted-payment-form
                v-if="recurringInformationChoice === 'credit-card'"
                :payment-source="
                    storedPaymentInformation.cpxCreditCardStoredPaymentSource
                        .data
                "
                class="max-w-[650px]"
                @token="handleRecurringToken"
            />

            <template
                v-if="recurringInformationChoice === 'credit-card-stripe'"
            >
                <stripe-form
                    v-if="recurring.creditCard.token === undefined"
                    ref="stripeFormRef"
                    :payment-source-id="
                        storedPaymentInformation
                            .cpxCreditCardStoredPaymentSource.id
                    "
                    class="max-w-[650px]"
                    @token="handleRecurringToken"
                />

                <div v-else>
                    <prime-message
                        severity="success"
                        :closable="false"
                    >
                        Stored Payment Method Added
                    </prime-message>
                </div>
            </template>

            <add-bank-account-form
                v-if="recurringInformationChoice === 'bank-account'"
                class="max-w-[650px]"
                :routing-number="recurring.bankAccount.routingNumber"
                :account-number="recurring.bankAccount.accountNumber"
                :account-number-confirm="
                    recurring.bankAccount.accountNumberConfirm
                "
                :account-type="recurring.bankAccount.accountType"
                @update:routing-number="
                    recurring.bankAccount.routingNumber = $event
                "
                @update:account-number="
                    recurring.bankAccount.accountNumber = $event
                "
                @update:account-number-confirm="
                    recurring.bankAccount.accountNumberConfirm = $event
                "
                @update:account-type="
                    recurring.bankAccount.accountType = $event
                "
            />

            <div
                v-if="recurringInformationChoice !== 'credit-card-stripe'"
                class="text-red-600 !m-0"
            >
                {{ errorRecurring || '&nbsp;' }}
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData, graphql } from '~/resources/graphql'
import { object, string } from 'yup'
import { Decimal } from '~/composables/support/decimal'

export const StoredPaymentInformationFacilityFragment = graphql(`
    fragment StoredPaymentInformationFacilityFragment on Facility {
        recurringAuthorizationMarkup(language: ENGLISH)
        cpxBankAccountStoredPaymentSource {
            id
        }
        cpxCreditCardStoredPaymentSource {
            id
            data {
                __typename
                ... on PaymentSourceCreditCard {
                    ...HostedPaymentFormPaymentSourceFragment
                }
            }
        }
    }
`)

export const RecurringInformationCustomerFragment = graphql(/* GraphQL */ `
    fragment RecurringInformationCustomerFragment on Customer {
        ...BillingAddressCustomerFragment
        newStoredPaymentInformation(facilityId: $facilityId) {
            facility {
                ...StoredPaymentInformationFacilityFragment
            }
        }
    }
`)

export const RecurringInformationFacilityFragment = graphql(/* GraphQL */ `
    fragment RecurringInformationFacilityFragment on Facility {
        ...StoredPaymentInformationFacilityFragment
        cpxPaymentSources {
            id
            data {
                __typename
                ... on PaymentSourceCreditCard {
                    ...HostedPaymentFormPaymentSourceFragment
                }
            }
        }
    }
`)

export const RecurringInformationSchema = object({
    total: string(),
    creditCard: object()
        .shape({
            paymentSourceId: string(),
            token: string().required(
                'Please enter your credit card details above.'
            )
        })
        .strict()
        .nullable(),
    bankAccount: object()
        .shape({
            paymentSourceId: string(),
            routingNumber: string().required(
                'Please enter your bank routing number'
            ),
            accountNumber: string().required(
                'Please enter your bank account number'
            ),
            accountNumberConfirm: string().required(
                'Please confirm your bank account number'
            ),
            accountType: string().required(
                'Please select the type of bank account'
            )
        })
        .strict()
        .nullable()
}).test(
    'Recurring Billing Details',
    'Please enter valid recurring billing details',
    value => {
        if (typeof value.total !== 'string') {
            return false
        }

        if (new Decimal(value.total).isZero()) {
            return true
        }

        if (value.creditCard !== null && value.creditCard.token !== undefined) {
            if (value.creditCard.token.length > 0) {
                return true
            }
        } else if (value.bankAccount !== null) {
            if (value.bankAccount.routingNumber === '') {
                return false
            }

            if (value.bankAccount.accountNumber === '') {
                return false
            }

            if (value.bankAccount.accountNumberConfirm === '') {
                return false
            }

            if (
                value.bankAccount.accountNumber !==
                value.bankAccount.accountNumberConfirm
            ) {
                return false
            }

            if (value.bankAccount.accountType === '') {
                return false
            }

            return true
        }

        return false
    }
)

export function RecurringInformationInitialValues(recurringTotal: string) {
    return {
        total: recurringTotal,
        creditCard: null,
        bankAccount: null
    }
}
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import HostedPaymentForm from '~/components/Payments/HostedPaymentForm.vue'
import PrimeButton from 'primevue/button'
import PrimeMessage from 'primevue/message'
import { type TypedSchema, useField } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/yup'
import RadioButton from 'primevue/radiobutton'
import { useShowAlert } from '~/composables/use-show-alert'
import AddBankAccountForm from '~/components/Checkout/AddBankAccountForm.vue'
import StripeForm from '~/components/Payments/StripeForm.vue'

const props = defineProps<{
    customer: FragmentType<typeof RecurringInformationCustomerFragment> | null
    facility: FragmentType<typeof RecurringInformationFacilityFragment>
    recurringTotal: Decimal
}>()

const facilityData = computed(() =>
    getFragmentData(RecurringInformationFacilityFragment, props.facility)
)

const customerData = computed(() =>
    props.customer === null
        ? null
        : getFragmentData(RecurringInformationCustomerFragment, props.customer)
)

const paymentInformationSchema = toTypedSchema(RecurringInformationSchema)
type RecurringInformationSchemaType =
    typeof paymentInformationSchema extends TypedSchema<infer TInput, unknown>
        ? TInput
        : never

const { value: recurring, errorMessage: errorRecurring } =
    useField<RecurringInformationSchemaType>('recurring', '', {
        validateOnValueUpdate: false
    })

const storedPaymentInformation = computed(() => {
    // If we have a customer, get the card information from that fragment
    if (customerData.value !== null) {
        return getFragmentData(
            StoredPaymentInformationFacilityFragment,
            customerData.value.newStoredPaymentInformation.facility
        )
    } else {
        return getFragmentData(
            StoredPaymentInformationFacilityFragment,
            facilityData.value
        )
    }
})

const showAlert = useShowAlert()
const recurringInformationOptions = computed(() => {
    const choices = []

    if (
        storedPaymentInformation.value.cpxCreditCardStoredPaymentSource !== null
    ) {
        if (
            storedPaymentInformation.value.cpxCreditCardStoredPaymentSource.data
                ?.__typename === 'PaymentSourceCreditCardStripe'
        ) {
            choices.push({
                key: 'credit-card-stripe',
                text: 'Credit Card'
            })
        } else {
            choices.push({
                key: 'credit-card',
                text: 'Enter a credit card'
            })
        }
    }

    if (
        storedPaymentInformation.value.cpxBankAccountStoredPaymentSource !==
        null
    ) {
        choices.push({
            key: 'bank-account',
            text: 'Enter a bank account'
        })
    }

    if (choices.length === 0) {
        showAlert(
            'Heads Up!',
            'Whoops! It looks like this checkout is incorrectly configured. Please reload, try again and contact the facility if this happens again.'
        )
    }

    return choices
})

const isDev = import.meta.env.DEV
const recurringInformationChoice = ref(
    recurringInformationOptions.value.some(value => value.key === 'credit-card')
        ? 'credit-card'
        : recurringInformationOptions.value.some(
              value => value.key === 'credit-card-stripe'
          )
        ? 'credit-card-stripe'
        : 'bank-account'
)

watch(
    recurringInformationChoice,
    newValue => {
        if (newValue === 'credit-card' || newValue === 'credit-card-stripe') {
            if (
                storedPaymentInformation.value
                    .cpxCreditCardStoredPaymentSource === null
            ) {
                throw new Error('Null Stored Payment Source')
            }

            recurring.value = {
                total: recurring.value.total,
                creditCard: {
                    paymentSourceId:
                        storedPaymentInformation.value
                            .cpxCreditCardStoredPaymentSource.id,
                    token: undefined
                },
                bankAccount: null
            }
        } else if (newValue === 'bank-account') {
            if (
                storedPaymentInformation.value
                    .cpxBankAccountStoredPaymentSource === null
            ) {
                throw new Error('Null Stored Payment Source')
            }

            recurring.value = {
                total: recurring.value.total,
                creditCard: null,
                bankAccount: {
                    paymentSourceId:
                        storedPaymentInformation.value
                            .cpxBankAccountStoredPaymentSource.id,
                    routingNumber: isDev ? '036001808' : '',
                    accountNumber: isDev ? '123456789' : '',
                    accountNumberConfirm: isDev ? '123456789' : '',
                    accountType: ''
                }
            }
        }
    },
    { immediate: true }
)

const showEditButton = computed(() => {
    if (
        recurringInformationChoice.value === 'credit-card-stripe' &&
        recurring.value.creditCard !== undefined &&
        recurring.value.creditCard !== null &&
        recurring.value.creditCard.token !== undefined
    ) {
        return true
    }

    return false
})

function handleEditButton() {
    // If credit card, wipe token
    if (recurring.value.creditCard !== null) {
        handleRecurringToken(undefined)
    }
}

function handleRecurringToken(token: string | undefined): void {
    if (
        storedPaymentInformation.value.cpxCreditCardStoredPaymentSource === null
    ) {
        throw new Error('Null Stored Payment Source')
    }

    recurring.value = {
        total: recurring.value.total,
        creditCard: {
            paymentSourceId:
                storedPaymentInformation.value.cpxCreditCardStoredPaymentSource
                    .id,
            token
        },
        bankAccount: null
    }
}

const stripeFormRef = ref<InstanceType<typeof StripeForm>>()
async function submitRecurringForm() {
    if (stripeFormRef.value !== undefined && stripeFormRef.value !== null) {
        await stripeFormRef.value.submitStripeForm()
    }
}

defineExpose({
    submitRecurringForm
})
</script>
